/**
 * MentionsInbox — bell-with-badge button + dropdown drawer (Inbox V2,
 * permanent since 2026-07-04): the badge counts ONLY open needs-you items
 * and the panel body is the shared <InboxSplit> (two lanes: Needs You /
 * Notifications).
 *
 * Render placement: sits in the sidebar/header. Counts poll every 30s.
 *
 * Mobile / tablet polish (2026-05-20; extended to tablet 2026-07-11): on
 * touch-class viewports (phone AND tablet, < lg / 1024px) the panel renders
 * as a bottom sheet (full-width, slides up from bottom) so it always uses the
 * full viewport and reads as a native sheet under the thumb; on desktop we
 * keep the original right-side popover. The popover variant relies on Radix
 * Portal (already enabled in our PopoverContent) so its DOM lives at body
 * level, not inside any drawer — overflow-hidden ancestors no longer clip it.
 * We also pin it with `collisionPadding` so it auto-flips into view if room is
 * tight. The sheet dismisses on tap-outside, the header X, or Escape.
 *
 * Controlled mode (2026-07-11): the mobile bottom-nav Inbox tab drives this
 * component via `open` / `onOpenChange` with `hideTrigger` (it renders its own
 * tab) — so the always-mounted bottom-nav instance is the single inbox WS
 * subscription on the mobile tab (proactive needs-you toast), replacing the
 * one that used to live in the flat-mobile drawer tree.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, X } from 'lucide-react';
import * as React from 'react';

import { InboxSplit } from '../inbox/inbox-split';
import { env } from '../../env';
import { useActiveOrg } from '../../lib/active-org';
import {
  inboxApi,
  inboxKeys,
  useInboxEvents,
  type InboxDataFrame,
} from '../../lib/api/inbox';
import type { InboxPage } from '../../lib/api/inbox';
import { useIsBelowDesktop } from '../../lib/use-media-query';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import { useToaster } from '../ui/toast';
import { PrivatePortal } from '../ui/private-portal';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';

interface MentionsInboxProps {
  collapsed?: boolean;
  /**
   * Visual placement of the bell trigger. The popover/sheet body and
   * behaviour are identical across variants — only the trigger chrome changes.
   *   - 'default' (sidebar / mobile drawer): full-height row, optional
   *     "Inbox" text label.
   *   - 'rail': a 32px icon-only button that matches the left ActivityBar rail
   *     footprint. Since 2026-07-11 the rail bell is the single desktop Inbox
   *     entry point (opens the popover as a right-side flyout).
   */
  variant?: 'default' | 'rail';
  /**
   * Controlled open state (optional). When provided, the caller owns the
   * open/close of the sheet — used by the mobile bottom-nav Inbox tab, which
   * drives this instance while rendering its own tab as the trigger. When
   * omitted the component keeps its own internal open state (bell trigger).
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Suppress the built-in bell trigger. The caller renders its own affordance
   * (e.g. the bottom-nav Inbox tab). The WS subscription + badge query still
   * run so the always-mounted instance stays live. Always presents as a sheet.
   */
  hideTrigger?: boolean;
}

export function MentionsInbox(props: MentionsInboxProps) {
  return <MentionsInboxV2 {...props} />;
}

/**
 * MentionsInboxV2 — same bell + popover/sheet chrome, but the badge counts ONLY
 * open needs-you items and the panel body is the shared <InboxSplit> (two lanes).
 */
function MentionsInboxV2({
  collapsed,
  variant = 'default',
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: MentionsInboxProps) {
  const me = useAuthStore((s) => s.user);
  // Touch-class = phone AND tablet (< lg / 1024px). Both get the bottom-sheet
  // presentation; only true desktop keeps the right-side popover flyout.
  const isBelowDesktop = useIsBelowDesktop();

  // Open state is controlled when the caller passes `open` (the mobile
  // bottom-nav tab); otherwise the component owns it (bell trigger).
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  // Present as a slide-up bottom sheet on any touch-class surface (phone +
  // tablet) OR whenever an external controller drives it (the bottom-nav,
  // which is a mobile surface). Desktop keeps the popover.
  const asSheet = isBelowDesktop || isControlled || hideTrigger;

  // The Inbox is GLOBAL — the bell badge counts open Needs You across ALL the
  // user's orgs, not just the active one. Only needs an authenticated user.
  const enabled = !!me;

  const countsQ = useQuery({
    queryKey: inboxKeys.countsGlobal(),
    queryFn: () => inboxApi.countsGlobal(),
    enabled,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const needsYou = countsQ.data ?? 0;

  // ── Proactive needs-you ping (shell-level) ──────────────────────────
  // This always-mounted bell is the SINGLE inbox WS subscription per tab
  // (InboxSplit no longer opens its own). On each frame the hook invalidates
  // the inbox query keys (badge + both lanes refetch live whether or not the
  // popover is open); on a fresh needs-you row it also calls onNeedsYou so the
  // founder gets a toast + OS Notification without having the Inbox open.
  const qc = useQueryClient();
  const { activeOrgId } = useActiveOrg();
  const { toast } = useToaster();
  // De-dup by mentionId so a reconnect burst (INSERT/UPDATE re-emit) can't
  // double-ping. Bounded so it can't grow without limit across a long session.
  const pingedRef = React.useRef<Set<string>>(new Set());

  const handleNeedsYou = React.useCallback(
    (frame: InboxDataFrame) => {
      const id = frame.mentionId;
      if (pingedRef.current.has(id)) return;
      pingedRef.current.add(id);
      if (pingedRef.current.size > 200) {
        // Drop the oldest ~half to keep the set bounded (insertion order).
        pingedRef.current = new Set(Array.from(pingedRef.current).slice(-100));
      }

      // Best-effort title from the just-invalidated needs-you list; the frame
      // itself carries no title, so a generic body is the fallback.
      const cached = qc.getQueryData<InboxPage>(inboxKeys.needsYouGlobal());
      const item = cached?.items.find((m) => m.id === id);
      const body = item?.title?.trim() || 'Open the Inbox to respond.';

      toast({ title: 'An agent needs you', description: body });

      if (
        typeof window !== 'undefined' &&
        typeof window.Notification === 'function' &&
        window.Notification.permission === 'granted'
      ) {
        try {
          // eslint-disable-next-line no-new
          new window.Notification('An agent needs you', { body, tag: id });
        } catch {
          // Notification construction can throw on some engines — ignore.
        }
      }
    },
    [qc, toast],
  );

  useInboxEvents({ enabled, orgId: activeOrgId, onNeedsYou: handleNeedsYou });

  // Sheet dismissal parity with the desktop popover: Escape closes it. The
  // Radix Popover handles Escape itself, so this only applies to the sheet.
  React.useEffect(() => {
    if (!asSheet || !open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [asSheet, open, setOpen]);

  // Lock background scroll while the sheet is open (same pattern as the mobile
  // board-picker / scope sheets) so the page behind doesn't scroll-bleed.
  React.useEffect(() => {
    if (!asSheet || !open || typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [asSheet, open]);

  if (!enabled) return null;

  const isRail = variant === 'rail';
  const trigger = hideTrigger ? null : (
    <button
      type="button"
      aria-label="Inbox"
      title={isRail ? 'Inbox' : undefined}
      data-testid="mentions-inbox-trigger"
      onClick={asSheet ? () => setOpen(true) : undefined}
      className={cn(
        isRail
          ? 'relative my-0.5 flex h-8 w-8 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground'
          : 'relative inline-flex items-center gap-2 rounded px-2 py-1 text-[13px] text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground pointer-coarse:min-h-11',
        !isRail && collapsed && 'justify-center',
      )}
    >
      <span className="relative">
        <Bell className="h-4 w-4" aria-hidden />
        {needsYou > 0 && (
          <span
            aria-hidden
            className="absolute -right-1.5 -top-1.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-semibold leading-none text-background"
            data-testid="mentions-badge"
          >
            {needsYou > 99 ? '99+' : needsYou}
          </span>
        )}
      </span>
      {!isRail && !collapsed && <span>Inbox</span>}
    </button>
  );

  const panelBody = (
    <>
      <header
        className={
          env.VITE_DESIGN_LIGHT
            ? 'flex items-center justify-between border-b dv2-hairline px-3 py-2'
            : 'flex items-center justify-between border-b border-border px-3 py-2'
        }
      >
        <span className="text-[13px] font-medium text-foreground">Inbox</span>
        {asSheet && (
          <button
            type="button"
            aria-label="Close inbox"
            onClick={() => setOpen(false)}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </header>
      <InboxSplit variant="panel" onNavigate={() => setOpen(false)} />
    </>
  );

  if (asSheet) {
    return (
      <>
        {trigger}
        <PrivatePortal>
            <div
              aria-hidden={!open}
              className={cn(
                'fixed inset-0 z-[60] transition-opacity duration-200',
                open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
              )}
            >
              <button
                type="button"
                aria-label="Close inbox"
                tabIndex={open ? 0 : -1}
                className="absolute inset-0 bg-foreground/50 backdrop-blur-sm"
                onClick={() => setOpen(false)}
              />
              <aside
                role="dialog"
                aria-modal={open}
                aria-label="Inbox"
                className={cn(
                  'absolute inset-x-0 bottom-0 flex max-h-[85dvh] flex-col rounded-t-xl border-t border-border bg-popover text-popover-foreground shadow-2xl transition-transform duration-300 ease-out',
                  'pb-safe',
                  open ? 'translate-y-0' : 'translate-y-full',
                )}
              >
                <div className="flex justify-center py-2">
                  <div className="h-1 w-10 rounded-full bg-foreground/15" aria-hidden />
                </div>
                {panelBody}
              </aside>
            </div>
        </PrivatePortal>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        side="right"
        sideOffset={8}
        avoidCollisions
        collisionPadding={8}
        className="flex h-[80dvh] max-h-[80dvh] w-[420px] max-w-[calc(100vw-2rem)] flex-col p-0"
      >
        {panelBody}
      </PopoverContent>
    </Popover>
  );
}
